import React, { useMemo } from 'react'
import NextImage from 'next/image'
import { Routes } from '@/assets/configs'
import { TFunction } from 'i18next'
import { useRouter } from 'next/router'
import { MobileSliderImage, StyledSlider } from '@/components/Profile/partials'
import { EmtChip, EmtFloatingIconButton } from 'emt-components-v2'
import LinkButton from '@/components/EMT/LinkButton'
import { useTranslation } from 'next-i18next'

import Blur from '@/assets/img/blur.png'
import useRNWebViewTranslation from '@/hooks/useRNWebViewTranslation'
import { getProfileActivityBadge } from '@/helpers/profileActivityBadge'

type Props = React.PropsWithChildren<{
  picturesUrls: string[]
  handleImageClick: (i: number) => void
  overrideBackButton?: () => void
  backButton?: boolean
  publicProfile?: boolean
  t?: TFunction<'profile', undefined>
  pro?: boolean
  color?: string
  userIsNew?: boolean
  userLastRefresh?: string
  reviewsCount?: number
  rating?: number
  distance?: number
  averageResponseTimeUnderThreeHours?: boolean
}>

const MobileHeader: React.FC<Props> = ({
  picturesUrls: [avatarImageUrl, ...profileImagesUrls],
  handleImageClick,
  overrideBackButton,
  backButton = true,
  publicProfile = false,
  t,
  pro = false,
  color,
  userIsNew = false,
  userLastRefresh,
  reviewsCount,
  rating,
  distance,
  averageResponseTimeUnderThreeHours,
}) => {
  const router = useRouter()
  const hasRNWebView = useRNWebViewTranslation()
  const { t: tc } = useTranslation('common')

  const activityBadge = React.useMemo(
    () =>
      getProfileActivityBadge({
        isNew: userIsNew,
        lastRefresh: userLastRefresh,
        reviewsCount,
        rating,
        distance,
        averageResponseTimeUnderThreeHours,
      }),
    [userIsNew, userLastRefresh, reviewsCount, rating, distance, averageResponseTimeUnderThreeHours],
  )

  const activityBadgeLabel = React.useMemo(
    () =>
      activityBadge
        ? tc(activityBadge.key, {
            defaultValue: activityBadge.defaultValue,
          })
        : undefined,
    [activityBadge, tc],
  )

  const newProfileImages = Array.isArray(profileImagesUrls)
    ? profileImagesUrls
    : profileImagesUrls
    ? Object.keys(profileImagesUrls).map((profileImageKey) => profileImagesUrls[profileImageKey])
    : undefined

  const settings = useMemo(
    () => ({
      arrows: false,
      dots: true,
      infinite: true,
      pagination: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
    }),
    [],
  )

  const visibleProfileImages = useMemo(
    () => (publicProfile ? (newProfileImages || []).slice(0, 3) : newProfileImages || []),
    [newProfileImages, publicProfile],
  )

  return (
    <>
      <div style={{ position: 'relative' }}>
        <StyledSlider {...settings}>
          {avatarImageUrl ? (
            <MobileSliderImage onClick={() => handleImageClick(0)}>
              <NextImage
                priority
                blurDataURL={Blur.blurDataURL}
                src={avatarImageUrl}
                layout="fill"
                sizes="100vw"
                alt={''}
              />
            </MobileSliderImage>
          ) : null}

          {visibleProfileImages.length
            ? visibleProfileImages.map((image, key) => (
                <MobileSliderImage key={key} onClick={() => handleImageClick(key + 1)}>
                  <NextImage
                    placeholder="blur"
                    blurDataURL={Blur.blurDataURL}
                    src={image}
                    layout="fill"
                    sizes="100vw"
                    alt={''}
                    className={
                      publicProfile && visibleProfileImages.length > 2 && key === 2 ? 'blur scale-105 z-[-1]' : ''
                    }
                  />
                  {publicProfile && visibleProfileImages.length > 2 && key === 2 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-[20px] pb-[40px]">
                      <p className="text-white text-[48px] font-bold ">+{visibleProfileImages.length - 2}</p>
                      {t && (
                        <div className="flex flex-col gap-[15px]">
                          <LinkButton
                            href={Routes.Connection.SignIn}
                            variant="whiteOutlined"
                            size={'medium'}
                            sx={{ width: '260px', maxWidth: '70vw' }}
                          >
                            {t('sign-in.title')}
                          </LinkButton>

                          <LinkButton
                            href={Routes.Connection.SignUp.Type}
                            size={'medium'}
                            sx={{ width: '260px', maxWidth: '70vw' }}
                          >
                            {t('sign-up.title')}
                          </LinkButton>
                        </div>
                      )}
                    </div>
                  )}
                </MobileSliderImage>
              ))
            : null}
        </StyledSlider>

        {activityBadgeLabel && (
          <EmtChip
            className="EmtReactNativeWebViewTop"
            label={activityBadgeLabel}
            color="transparent"
            size="small"
            sx={{
              position: 'absolute',
              top: 22,
              right: 22,
              zIndex: 10,
              fontWeight: 600,
              '& .MuiChip-label': {
                padding: '0 8px',
              },
            }}
          />
        )}
      </div>
      {backButton && (
        <EmtFloatingIconButton
          boxShadow
          iconClass="emt-arrow-left"
          sx={{
            position: 'fixed',
            marginTop: hasRNWebView ? '5px' : '0',
            zIndex: 10,
            top: 22,
            left: 22,
            cursor: 'pointer',
            width: '40px',
            height: '40px',
            '& .emt-icons': { fontSize: '18px', color: color || 'primary' },
          }}
          className="EmtReactNativeWebViewTop"
          onClick={() => {
            if (overrideBackButton) {
              overrideBackButton()
            } else {
              router.asPath.includes('utm_source')
                ? router.replace(Routes.Search._pathname)
                : window.history.length > 1
                ? router.back()
                : router.replace(Routes.Search._pathname)
            }
          }}
        />
      )}
    </>
  )
}

export default MobileHeader
